import { mortgagePayment, mortgageAffordability, minimumDownPayment, PAYMENT_FREQUENCIES } from "../../lib/calculators/mortgage";
import { CAD, CAD2, num } from "../../lib/format";
import { MORTGAGE_RULES } from "../../lib/financial-data/canada";

const freqOptions = Object.entries(PAYMENT_FREQUENCIES).map(([value, v]) => ({ value, label: v.label }));

export const mortgagePaymentConfig = {
  slug: "mortgage-payment-calculator-canada",
  name: "Mortgage Payment Calculator",
  category: "mortgage",
  icon: "Home",
  tagline: "Estimate your Canadian mortgage payment and full amortization schedule.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "mortgage-planner",
  relatedTools: ["mortgage-affordability-calculator-canada", "compound-interest-calculator-canada"],
  seo: {
    title: "Mortgage Payment Calculator Canada (2026) — Amortization & Accelerated Payments",
    description:
      "Free Canadian mortgage payment calculator with semi-annual compounding, full amortization schedule, and accelerated bi-weekly options. See total interest and payoff time.",
    keywords: ["mortgage payment calculator canada", "canadian mortgage calculator", "accelerated biweekly mortgage", "amortization schedule canada"],
  },
  inputs: [
    { id: "homePrice", label: "Home price", type: "currency", default: 600000, min: 0, help: "The purchase price of the home." },
    { id: "downPayment", label: "Down payment", type: "currency", default: 120000, min: 0, help: "Amount you pay up front." },
    { id: "annualRate", label: "Interest rate", type: "percent", default: 4.79, min: 0, max: 25, step: 0.01, suffix: "%", help: "Your annual mortgage interest rate." },
    { id: "amortizationYears", label: "Amortization", type: "slider", default: 25, min: 5, max: 30, step: 1, suffix: " yrs", help: "Total years to pay off the mortgage." },
    { id: "frequency", label: "Payment frequency", type: "select", default: "monthly", options: freqOptions, help: "How often you make payments. Accelerated options pay off faster." },
  ],
  compute: (v) => {
    const principal = Math.max(0, (v.homePrice || 0) - (v.downPayment || 0));
    const r = mortgagePayment({ principal, annualRate: v.annualRate, amortizationYears: v.amortizationYears, frequency: v.frequency });
    const downPct = v.homePrice ? (v.downPayment / v.homePrice) * 100 : 0;
    return {
      primary: { label: `${r.frequencyLabel} payment`, value: r.payment, type: "currency2" },
      secondary: [
        { label: "Mortgage amount", value: principal, type: "currency" },
        { label: "Total interest", value: r.totalInterest, type: "currency" },
        { label: "Total paid", value: r.totalPaid, type: "currency" },
        { label: "Payoff time", value: r.payoffYears, type: "years" },
        { label: "Number of payments", value: r.numberOfPayments, type: "number" },
        { label: "Down payment", value: `${num(downPct, 1)}%`, type: "raw" },
      ],
      chart: {
        type: "pie",
        title: "Principal vs interest",
        data: [
          { name: "Principal", value: Math.round(principal) },
          { name: "Interest", value: Math.round(r.totalInterest) },
        ],
      },
      chart2: {
        type: "stackedArea",
        title: "Balance over time",
        data: r.yearly.map((y) => ({ x: `Yr ${y.year}`, Balance: y.balance })),
        xKey: "x",
        series: [{ key: "Balance", name: "Remaining balance" }],
      },
      schedule: {
        title: "Amortization schedule (yearly)",
        columns: ["Year", "Principal", "Interest", "Balance"],
        rows: r.yearly.map((y) => [y.year, CAD(y.principal), CAD(y.interest), CAD(y.balance)]),
      },
      downloadRows: () => [
        ["Payment #", "Payment", "Principal", "Interest", "Balance"],
        ...r.schedule.map((s) => [s.period, s.payment, s.principal, s.interest, s.balance]),
      ],
    };
  },
  content: {
    howItWorks:
      "Canadian fixed-rate mortgages compound interest semi-annually (not monthly like in the U.S.). This calculator converts your annual rate into the correct effective periodic rate for your chosen payment frequency, then amortizes the loan payment by payment. Accelerated bi-weekly and weekly options take your monthly payment and split it, so you make the equivalent of one extra monthly payment per year — shortening your amortization and cutting interest.",
    inputsExplained: [
      { term: "Home price", desc: "The purchase price of the property." },
      { term: "Down payment", desc: "Your up-front cash. Canadian rules require a minimum 5% on the first $500k, 10% on the portion to $1M, and 20% at/above $1M." },
      { term: "Interest rate", desc: "Your annual contract rate. Fixed Canadian mortgages compound semi-annually." },
      { term: "Amortization", desc: "The total length of time to repay the mortgage, commonly 25 years." },
      { term: "Payment frequency", desc: "Accelerated bi-weekly/weekly options pay off the mortgage faster and save interest." },
    ],
    example:
      "A $480,000 mortgage (on a $600,000 home with 20% down) at 4.79% over 25 years works out to roughly $2,730/month. Switching to accelerated bi-weekly can save tens of thousands in interest and shave years off the amortization.",
    assumptions: [
      "Fixed interest rate for the full amortization (real mortgages renew every term).",
      "Semi-annual compounding per Canadian convention.",
      "No property tax, insurance, or CMHC premium included in the payment.",
    ],
    limitations: [
      "Does not include CMHC mortgage default insurance premiums.",
      "Does not model variable rates, renewals, or rate changes.",
      "Property tax and heating are excluded — see the Affordability calculator for those.",
    ],
  },
  faq: [
    { q: "How is a Canadian mortgage payment different from a US one?", a: "Canadian fixed mortgages compound semi-annually, while US mortgages typically compound monthly. This calculator uses the Canadian convention, so the effective rate — and your payment — differ slightly." },
    { q: "What is accelerated bi-weekly?", a: "Your monthly payment is divided by two and paid every two weeks. Because there are 26 bi-weekly periods, you make the equivalent of 13 monthly payments per year, which pays the mortgage off faster." },
    { q: "Does this include CMHC insurance?", a: "No. If your down payment is under 20%, CMHC insurance premiums apply and are usually added to the mortgage. Factor those in separately." },
  ],
};

export const mortgageAffordabilityConfig = {
  slug: "mortgage-affordability-calculator-canada",
  name: "Mortgage Affordability Calculator",
  category: "mortgage",
  icon: "Home",
  tagline: "Estimate how much home you might afford using Canadian GDS/TDS guidelines.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "mortgage-planner",
  relatedTools: ["mortgage-payment-calculator-canada", "budget-calculator-canada"],
  seo: {
    title: "Mortgage Affordability Calculator Canada (2026) — GDS/TDS & Stress Test",
    description:
      "Estimate how much home you can afford in Canada using GDS/TDS ratios and the mortgage stress test. Educational estimate — not a pre-approval.",
    keywords: ["mortgage affordability calculator canada", "how much house can i afford canada", "gds tds calculator", "mortgage stress test"],
  },
  inputs: [
    { id: "annualIncome", label: "Household annual income (before tax)", type: "currency", default: 120000, min: 0 },
    { id: "monthlyDebts", label: "Other monthly debt payments", type: "currency", default: 500, min: 0, help: "Car loans, credit cards, student loans, etc." },
    { id: "downPayment", label: "Down payment saved", type: "currency", default: 80000, min: 0 },
    { id: "annualRate", label: "Mortgage rate", type: "percent", default: 4.79, min: 0, max: 25, step: 0.01, suffix: "%" },
    { id: "amortizationYears", label: "Amortization", type: "slider", default: 25, min: 5, max: 30, step: 1, suffix: " yrs" },
    { id: "propertyTaxAnnual", label: "Estimated property tax (annual)", type: "currency", default: 4000, min: 0 },
    { id: "heatingMonthly", label: "Estimated heating (monthly)", type: "currency", default: 150, min: 0 },
    { id: "useStressTest", label: "Apply mortgage stress test", type: "select", default: "yes", options: [{ value: "yes", label: "Yes (recommended)" }, { value: "no", label: "No" }] },
  ],
  compute: (v) => {
    const r = mortgageAffordability({
      annualIncome: v.annualIncome,
      monthlyDebts: v.monthlyDebts,
      downPayment: v.downPayment,
      annualRate: v.annualRate,
      amortizationYears: v.amortizationYears,
      propertyTaxAnnual: v.propertyTaxAnnual,
      heatingMonthly: v.heatingMonthly,
      useStressTest: v.useStressTest !== "no",
    });
    const minDown = minimumDownPayment(r.maxHomePrice);
    const downShort = Math.max(0, minDown - (v.downPayment || 0));
    return {
      primary: { label: "Estimated maximum home price", value: r.maxHomePrice, type: "currency" },
      secondary: [
        { label: "Maximum mortgage", value: r.maxMortgage, type: "currency" },
        { label: "Max monthly payment (P&I)", value: r.maxMonthlyPayment, type: "currency2" },
        { label: "Qualifying rate used", value: r.qualifyingRate, type: "percent", hint: "Stress-tested rate" },
        { label: "Min. down for this price", value: minDown, type: "currency" },
        { label: "Down payment shortfall", value: downShort, type: "currency" },
        { label: "GDS room (monthly)", value: r.gdsRoom, type: "currency2" },
      ],
      chart: {
        type: "bar",
        title: "Home price = down payment + mortgage",
        data: [
          { x: "Down payment", Amount: Math.round(v.downPayment || 0) },
          { x: "Max mortgage", Amount: Math.round(r.maxMortgage) },
        ],
        xKey: "x",
        series: [{ key: "Amount", name: "Amount" }],
      },
    };
  },
  content: {
    howItWorks:
      "Canadian lenders use two ratios to decide how much you can borrow. Gross Debt Service (GDS) says your housing costs — mortgage payment, property tax and heating — should stay under about 39% of gross income. Total Debt Service (TDS) says all debt payments combined should stay under about 44%. Lenders must also apply the mortgage 'stress test', qualifying you at the greater of your contract rate + 2% or 5.25%. This tool applies those guidelines to estimate a maximum home price.",
    inputsExplained: [
      { term: "Household income", desc: "Combined gross (pre-tax) annual income of all applicants." },
      { term: "Other monthly debts", desc: "Minimum payments on car loans, credit cards, lines of credit and student loans." },
      { term: "Stress test", desc: "Qualifying at a higher rate to ensure you can handle future rate increases." },
    ],
    example:
      "A household earning $120,000 with $500/month of other debt and $80,000 down might qualify for roughly a $500,000–$600,000 home, depending on rate and property taxes.",
    assumptions: [
      "GDS limit 39% and TDS limit 44% (typical lender guidelines).",
      "Stress test at max(contract + 2%, 5.25%).",
      "Estimates only — actual approval depends on credit, employment and the specific lender.",
    ],
    limitations: [
      "This is an educational estimate, NOT a mortgage pre-approval or approval.",
      "Does not include CMHC insurance, closing costs, or condo fees.",
      "Lender policies vary; always confirm with a mortgage professional.",
    ],
  },
  faq: [
    { q: "Is this a pre-approval?", a: "No. This is an educational estimate based on standard GDS/TDS guidelines and the stress test. Only a lender can pre-approve you." },
    { q: "What are GDS and TDS?", a: "Gross Debt Service and Total Debt Service ratios. GDS covers housing costs; TDS covers all debts. Lenders cap these to protect against over-borrowing." },
    { q: "Why is the qualifying rate higher than my rate?", a: "The federal stress test requires qualifying at the greater of your contract rate plus 2% or 5.25%, so you can still afford payments if rates rise." },
  ],
};
